import * as React from 'react';
import { Box, createStyles, makeStyles, Theme } from '@material-ui/core';
import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ApplicationState } from '../../store';
import { Dispatch } from 'redux';
import {
  resetEndDateTimeFilter,
  resetStartDateTimeFilter,
} from '../../store/Alerts/AlertsActions';
import { ApiEnums } from '../../Interfaces/ApiEnums';

// Components

import StringResourceManager, { GetString } from '../StringResourceManager';
import { WeightAlertGroupRecord } from '../../Interfaces/Alerts/AlertRecord';
import WidgetTotalCountTile from '../WidgetTotalCountTile';
import { ChartSeriesInterface } from '../../Interfaces/Devices/ChartSeriesInterface';
import OverviewWidgetChart from '../OverviewWidgetChart';
import OverviewWidgetPieChart from '../OverviewWidgetPieChart';

import {
  SeriesClickEvent,
  LegendItemClickEvent,
} from '@progress/kendo-react-charts';
import { State } from '@progress/kendo-data-query';
import { ASSET } from '../../utils/constants';
import { saveAlertsGridState } from '../../store/UserSettings/UserSettingsActions';
import { fetchJsonData } from '../../utils/utils';

// Styles
const useStylesAlertsTotal = makeStyles((theme: Theme) =>
  createStyles({
    widgetBox: {
      margin: theme.spacing(2),
      width: '450px',
    },
    flexBox: {
      flex: 1,
      flexDirection: 'row',
      display: 'flex',
    },
    widgetItem: {
      flex: 1,
    },
    tileContainer: {
      display: 'flex',
      width: '100%',
      justifyContent: 'center',
    },
    totalCountContainer: {
      display: 'flex',
      height: 140,
      width: '200px',
    },
    overViewTitle: {
      display: 'flex',
      justifyContent: 'center',
    },
  })
);

export interface AlertCountByStore {
  store: string;
  count: number;
}

export interface AlertCountByType {
  category: ApiEnums.AlertType;
  label: string;
  value: number;
}

const fetchAllNewAlerts = async (
  startDateTime: Date,
  endDateTime: Date,
  token: string | undefined
) => {
  const allAlerts = await fetchJsonData(
    `alertrecords/getallnewalerts/${startDateTime.toISOString()}/${endDateTime.toISOString()}`,
    token
  );
  return allAlerts as WeightAlertGroupRecord[];
};

export default function AlertsTotal() {
  const classes = useStylesAlertsTotal();
  const [refresh, setRefresh] = useState<boolean>(true);

  const [allAlerts, setAllAlerts] = useState<WeightAlertGroupRecord[]>([]);

  const isLoadingAllAlerts: boolean = useSelector(
    (state: ApplicationState) => state.alertsReducer.isLoadingAllAlerts
  );

  const startDateTimeFilter: Date = useSelector(
    (state: ApplicationState) => state.alertsReducer.startDateTimeFilter
  );

  const token: string | undefined = useSelector(
    (state: ApplicationState) => state.userSessionReducer.sessionInfo?.Token
  );

  const dispatch: Dispatch<any> = useDispatch();

  const [storeList, setStoreList] = React.useState<string[]>([]);

  const [alertsCountByStore, setAlertsCountByStore] = React.useState<
    AlertCountByStore[]
  >([]);

  const [alertsCountByType, setAlertsCountByType] = React.useState<
    AlertCountByType[]
  >([]);

  const [alertsCountByStoreSeriesData, setAlertsCountByStoreSeriesData] =
    React.useState<ChartSeriesInterface[]>([]);

  const selectedLanguage: string = useSelector(
    (state: ApplicationState) => state.userSettingsReducer.selectedLanguage
  );

  useEffect(() => {
    let isMounted = true;
    setAlertsCountByType([]);
    fetchAllNewAlerts(startDateTimeFilter, new Date(), token).then(data => {
      if (isMounted) setAllAlerts(data);
    });
    return () => {
      isMounted = false;
    };
  }, [selectedLanguage]);

  useEffect(() => {
    resetStartDateTimeFilter(dispatch);
    resetEndDateTimeFilter(dispatch);
    return () => {};
  }, []);

  useEffect((): ReturnType<React.EffectCallback> => {
    if (refresh) {
      setRefresh(false);
    }
    return (): void => {};
  }, [refresh]);

  useEffect((): ReturnType<React.EffectCallback> => {
    let list: AlertCountByStore[] = [];
    const uniqueStoreList = allAlerts
      .map((item: WeightAlertGroupRecord) => item.SiteNumber)
      .filter((value, index, self) => self.indexOf(value) === index);

    for (let i = 0; i < uniqueStoreList.length; ++i) {
      let data: AlertCountByStore = {
        store: uniqueStoreList[i],
        count: 0,
      };

      list.push(data);
    }

    setAlertsCountByStore(list);
    return (): void => {};
  }, [allAlerts]);

  useEffect((): ReturnType<React.EffectCallback> => {
    let seriesData: ChartSeriesInterface[] = [
      {
        status: undefined,
        count: [],
        color: 'red',
      },
    ];
    let alertsCountByType: AlertCountByType[] = [];

    if (alertsCountByStore.length == 0) {
      setAlertsCountByStoreSeriesData(seriesData);
      return;
    }

    let storeList: string[] = [];
    let list: AlertCountByStore[] = [...alertsCountByStore];

    for (let i = 0; i < allAlerts.length; ++i) {
      let index = alertsCountByStore.findIndex(
        (a: AlertCountByStore) => a.store === allAlerts[i].SiteNumber
      );

      list[index].count++;

      if (allAlerts[i].CommandStatus == ApiEnums.CommandStatus.New) {
        const indexAlertCountByType = alertsCountByType.findIndex(
          (a: AlertCountByType) => a.category === allAlerts[i].AlertType
        );

        if (indexAlertCountByType == -1) {
          const alertCountByType: AlertCountByType = {
            category: allAlerts[i].AlertType,
            label: getCategoryLabel(allAlerts[i].AlertType),
            value: 1,
          };
          alertsCountByType.push(alertCountByType);
        } else {
          alertsCountByType[indexAlertCountByType].value++;
        }
      }
    }

    list.sort((s1, s2) =>
      s1.count < s2.count ? 1 : s1.count > s2.count ? -1 : 0
    );

    const top5Stores = list.slice(0, 5);

    for (let i = 0; i < top5Stores.length; ++i) {
      if (top5Stores[i].count == 0) continue;

      storeList.push(top5Stores[i].store);
      seriesData[0].count.push(top5Stores[i].count);
    }

    setStoreList(storeList);
    setAlertsCountByStoreSeriesData(seriesData);
    setAlertsCountByType(alertsCountByType);
    return (): void => {};
  }, [alertsCountByStore]);

  const maxChartValueAxisItem: any = () => {
    let value: number = 10;

    if (
      alertsCountByStoreSeriesData.length > 0 &&
      alertsCountByStoreSeriesData[0].count.length > 0
    )
      value = alertsCountByStoreSeriesData[0].count[0] + 10;

    return value;
  };

  const onAlertsByTypeSeriesClick = (event: SeriesClickEvent) => {
    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'AlertType',
            operator: 'eq',
            value: event.dataItem.category,
          },
          {
            field: 'CommandStatus',
            operator: 'eq',
            value: ApiEnums.CommandStatus.New,
          },
        ],
      },
    };

    saveAlertsGridState(dispatch, gridState);
  };

  const onAlertsByTypeLegendClick = (event: LegendItemClickEvent) => {
    let series: any = event.series;
    let alertType: ApiEnums.AlertType = series.data[event.pointIndex].category;
    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'AlertType',
            operator: 'eq',
            value: alertType,
          },
          {
            field: 'CommandStatus',
            operator: 'eq',
            value: ApiEnums.CommandStatus.New,
          },
        ],
      },
    };

    saveAlertsGridState(dispatch, gridState);
  };

  const labelContent = (props: any) => props.dataItem.value;

  const MIXED_LIGHT_HEAVY_STR = GetString('MIXED_LIGHT_HEAVY_STR');
  const MIXED_ZERO_HEAVY_STR = GetString('MIXED_ZERO_HEAVY_STR');
  const WEIGHT_OVERRIDDEN_STR = GetString('WEIGHT_OVERRIDDEN_STR');
  const UNKNOWN_STR = GetString('UNKNOWN_STR');

  const getCategoryLabel = (value: ApiEnums.AlertType) => {
    switch (value) {
      case ApiEnums.AlertType.Unknown:
        return UNKNOWN_STR;
      case ApiEnums.AlertType.MixedLightHeavyWeights:
        return MIXED_LIGHT_HEAVY_STR;
      case ApiEnums.AlertType.MixedZeroHeavyWeights:
        return MIXED_ZERO_HEAVY_STR;
      case ApiEnums.AlertType.WeightOverridden:
        return WEIGHT_OVERRIDDEN_STR;
      default:
        return UNKNOWN_STR;
    }
  };

  const getAlertsTileColor = (count: number) => {
    if (count <= 0) return 'green';
    else if (count <= 50) return '#FFB600';
    else return 'red';
  };

  const onStoreNumberSeriesItemClick = (event: SeriesClickEvent) => {
    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: ASSET.FIELD_STORE_NAME,
            operator: 'eq',
            value: event.category,
          },
          {
            field: 'CommandStatus',
            operator: 'eq',
            value: ApiEnums.CommandStatus.New,
          },
        ],
      },
    };

    saveAlertsGridState(dispatch, gridState);
  };

  const widgetNewAlertsCountTileClick = () => {
    let gridState: State = {
      filter: {
        logic: 'and',
        filters: [
          {
            field: 'CommandStatus',
            operator: 'eq',
            value: ApiEnums.CommandStatus.New,
          },
        ],
      },
    };

    saveAlertsGridState(dispatch, gridState);
  };

  return (
    <React.Fragment>
      <Box className={classes.widgetBox}>
        <div className={classes.overViewTitle} data-testid="alerts-total-root">
          <StringResourceManager
            stringKey="ALERTS_SUMMARY"
            style="overViewTitle"
          />
        </div>
        <div className={classes.tileContainer}>
          <div className={classes.totalCountContainer}>
            <WidgetTotalCountTile
              title={GetString('NEW_ALERTS')}
              count={allAlerts.length}
              bgColor={getAlertsTileColor(allAlerts.length)}
              textColor="#fff"
              goToScreen="alerts"
              onClick={widgetNewAlertsCountTileClick}
              testId="overview_alerts_newAlertsCount"
            />
          </div>
        </div>
        <div className={classes.flexBox}>
          <div className={classes.widgetItem}>
            <OverviewWidgetChart
              chartTitleText={GetString('TOP_STORES_WITH_ALERTS')}
              chartValueAxisItemtitle={GetString('NUMBER_OF_ALERTS')}
              chartCategoryAxisItem={storeList}
              maxChartValueAxisItem={maxChartValueAxisItem()}
              seriesData={alertsCountByStoreSeriesData}
              chartSeriesClick={onStoreNumberSeriesItemClick}
              goToScreen={'alerts'}
              chartType="bar"
              isRefreshing={isLoadingAllAlerts}
            />
            <OverviewWidgetPieChart
              chartTitleText={GetString('OPEN_ALERTS_BY_TYPE')}
              seriesData={alertsCountByType}
              chartSeriesField={'value'}
              chartSeriesCategoryField={'label'}
              chartSeriesClick={onAlertsByTypeSeriesClick}
              legendItemClick={onAlertsByTypeLegendClick}
              labelContent={labelContent}
              goToScreen="alerts"
              isRefreshing={isLoadingAllAlerts}
            />
          </div>
        </div>
      </Box>
    </React.Fragment>
  );
}
